import {
  createClockChatItem,
  createOutputTextItem,
} from '../ChatItem/functions';
import openAppCommand from '../commands/appCommands/openAppCommand';
import handleAddCalendarItemCommand from '../commands/calendarCommands/addCalendarItemCommand';
import deleteCalendarItemCommand from '../commands/calendarCommands/deleteCalendarItemCommand';
import handleCalendarItemCommand from '../commands/calendarCommands/handleCalendarCommands';
import updateCalendarItemCommand from '../commands/calendarCommands/updateCalendarItem';
import { addContactCommand } from '../commands/contactCommands/addContactCommand';
import handleCallContactCommand from '../commands/contactCommands/callContactCommand';
import processIncrementGoalCommand from '../commands/goalCommands/incrementGoal';
import addListItemCommand from '../commands/listCommands/addListItemCommand';
import deleteListItemCommand from '../commands/listCommands/deleteListItemCommand';
import showListCommand from '../commands/listCommands/showListCommand';
import handleRecipeItemCommand from '../commands/recipeCommands/handleRecipeCommands';
import handlePhoneCommand from '../commands/systemCommands/handlePhoneCommand';
import { ChatCommand, CommandTypes } from '../commands/types';
import { handleShowWeatherCommand } from '../commands/weatherCommands/handleWeatherCommand';
import { getLastOutputTextMessage, getLastUserMessage } from '../functions';

import { listCalendarItemsByHabitat } from '@akin-home/calendar/combined';
import { useCommands } from '@akin-home/commands-react';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { UserModel } from '../../auth/UserModel';
import { startVoiceListening } from '../../speech-to-text/useSTT';
import { useStateStore } from '../../stores/useStateStore';
import { ChatItem } from '../ChatItem/types';
import { handleCheckCalendarItemsCommand } from '../commands/calendarCommands/checkCalendarItemsCommand';
import { handleCreateChecklistCommand } from '../commands/checklistCommands.ts/createChecklistCommand';
import { deleteChecklistCommand } from '../commands/checklistCommands.ts/deleteChecklistCommand';
import { handleOpenFocusedChatCommand } from '../commands/focusedChatCommands/focused_chat_commands';
import { handleCreateGoalCommand } from '../commands/goalCommands/createGoalCommand';
import { handleFollowupInputCommand } from '../commands/handleFollowupInputCommand';
import { refetchStateVariables } from '../commands/refetchStateVariables';
import { processUpdateVoiceCommand } from '../commands/voiceCommands/updateVoiceCommand';

type Params = {
  user: UserModel;
  addItem(item: ChatItem | ChatItem[]): void;
};

export const useGlobalCommands = (params: Params) => {
  const { user, addItem } = params;

  const query_client = useQueryClient();

  const modifyLastItemResponse = useStateStore(
    (state) => state.modifyLastItemResponse
  );

  useCommands(
    global_commands_list,
    useCallback(
      (commands: ChatCommand[]) => {
        const habitatID = user.habitatID;

        commands.forEach(async (cmd) => {
          let returnedMsg = '';

          if (cmd.type === CommandTypes.FOLLOW_UP_INPUT && cmd.data.input) {
            handleFollowupInputCommand(cmd);
          } else if (cmd.type === CommandTypes.REFETCH_STATE_VARIABLES) {
            refetchStateVariables({ query_client });
          } else if (cmd.type === CommandTypes.PHONE) {
            handlePhoneCommand(cmd);
          } else if (cmd.type === 'show_weather') {
            handleShowWeatherCommand({ command: cmd, addItem });
          } else if (cmd.type === 'get_time_location') {
            addItem(
              createClockChatItem(
                cmd.data.Location,
                cmd.data.Date,
                cmd.data.Time
              )
            );
          } else if (cmd.type === 'add_calendar_item') {
            handleAddCalendarItemCommand(cmd);
          } else if (cmd.type === 'delete_calendar_item') {
            deleteCalendarItemCommand(
              cmd,
              user.habitatID,
              query_client,
              addItem
            );
          } else if (cmd.type === 'update_calendar_item') {
            returnedMsg = await updateCalendarItemCommand(
              cmd.data,
              user.habitatID,
              query_client
            );
          } else if (cmd.type === CommandTypes.ADD_LIST_ITEM) {
            await addListItemCommand(cmd, addItem);
          } else if (cmd.type === CommandTypes.DELETE_LIST_ITEM) {
            deleteListItemCommand(cmd, addItem);
          } else if (cmd.type === CommandTypes.SHOW_CHECKLIST) {
            showListCommand(cmd, addItem);
          } else if (cmd.type === 'repeat_watson_message') {
            const lastOutputTextMessage = getLastOutputTextMessage(
              useStateStore.getState().chat_items
            );

            if (lastOutputTextMessage) {
              addItem(
                createOutputTextItem(
                  `The last thing I said was:\n${lastOutputTextMessage.data.text}`
                )
              );
            }
          } else if (cmd.type === 'repeat_user_message') {
            // Get the last text chat item submitted by the user
            const lastUserTextMessage = getLastUserMessage(
              useStateStore.getState().chat_items
            );

            if (lastUserTextMessage) {
              addItem(
                createOutputTextItem(
                  `The last thing you said was:\n${lastUserTextMessage.data.text}`
                )
              );
            }
          } else if (cmd.type === 'check_calendar_items') {
            handleCheckCalendarItemsCommand({
              command: cmd,
              addItems: addItem,
              getCalendarItems(params) {
                const { from, to, title, type } = params;

                return listCalendarItemsByHabitat({
                  types: type ? [type] : undefined,
                  query: {
                    habitatID,
                    filter: {
                      title,
                      from,
                      to,
                    },
                  },
                });
              },
            });
          } else if (cmd.type === 'show_calendar_item') {
            returnedMsg = await handleCalendarItemCommand(cmd, {
              addItem,
              habitatID,
            });
          } else if (cmd.type === 'show_calendar_items_today') {
            returnedMsg = await handleCalendarItemCommand(cmd, {
              addItem,
              habitatID,
            });
          } else if (cmd.type === 'show_calendar_items') {
            returnedMsg = await handleCalendarItemCommand(cmd, {
              addItem,
              habitatID,
            });
          } else if (cmd.type === 'show_recipe_item') {
            handleRecipeItemCommand(cmd, {
              addItem,
            });
          } else if (cmd.type === 'show_quick_recipe_item') {
            handleRecipeItemCommand(cmd, {
              addItem,
            });
          } else if (cmd.type === 'show_easy_recipe_item') {
            handleRecipeItemCommand(cmd, {
              addItem,
            });
          } else if (cmd.type === 'update_goal') {
            processIncrementGoalCommand({
              command: cmd,
              user,
              queryClient: query_client,
              addItem,
            });
          } else if (cmd.type === 'call_contact') {
            handleCallContactCommand({
              habitatID,
              addItem,
              command: cmd,
            });
          } else if (cmd.type === 'add_contact') {
            addContactCommand({
              habitatID,
              addItem,
              command: cmd,
              query_client,
            });
          } else if (cmd.type === 'open_app') {
            openAppCommand({ command: cmd, postToChat: addItem });
          } else if (cmd.type === CommandTypes.ACTIVATE_MIC) {
            if (cmd.data?.delay) {
              try {
                setTimeout(() => {
                  startVoiceListening();
                }, parseInt(cmd.data.delay));
              } catch (ex) {
                console.log(ex);
              }
            } else {
              startVoiceListening();
            }
          } else if (cmd.type === 'create_goal') {
            handleCreateGoalCommand({
              user_id: user.id,
              habitatID,
              data: cmd.data,
            });
          } else if (cmd.type === 'create_checklist') {
            handleCreateChecklistCommand({
              user_id: user.id,
              habitatID,
              data: cmd.data,
            });
          } else if (cmd.type === CommandTypes.DELETE_CHECKLIST) {
            deleteChecklistCommand({ command: cmd, addItem });
          } else if (cmd.type === 'open_focused_chat') {
            handleOpenFocusedChatCommand({ cmd });
          } else if (cmd.type === 'update_voice') {
            processUpdateVoiceCommand({ command: cmd });
          }

          if (returnedMsg.length > 0) {
            modifyLastItemResponse(returnedMsg);
          }
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [query_client, user]
    )
  );
};

const global_commands_list = [
  CommandTypes.FOLLOW_UP_INPUT,
  CommandTypes.PHONE,
  CommandTypes.CHECK_CALENDAR_ITEMS,
  'show_weather',
  'get_time_location',
  'add_calendar_item',
  'delete_calendar_item',
  'update_calendar_item',
  'delete_list_item',
  'repeat_watson_message',
  'repeat_user_message',
  'show_calendar_item',
  'show_calendar_items_today',
  'show_calendar_items',
  'show_recipe_item',
  'show_quick_recipe_item',
  'show_easy_recipe_item',
  'update_goal',
  'call_contact',
  'add_contact',
  'open_app',
  'create_goal',
  'create_checklist',
  CommandTypes.ADD_LIST_ITEM,
  CommandTypes.SHOW_CHECKLIST,
  CommandTypes.DELETE_CHECKLIST,
  CommandTypes.DELETE_LIST_ITEM,
  'refetch_state_variables',
  'open_focused_chat',
  'update_voice',
];
